import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Download, FileText, Share2, Trash2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHeader, StatusBadge } from "../components/Bits";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, money, downloadFile, discLabel, STATUSES } from "../lib/api";

const sharePdf = async (o) => {
  try {
    const res = await api.get(`/orders/${o.id}/pdf`, { responseType: "blob" });
    const file = new File([res.data], `${o.order_no}.pdf`, { type: "application/pdf" });
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: `Ekol Temizlik - ${o.order_no}` });
    } else {
      const href = URL.createObjectURL(file);
      window.open(href, "_blank");
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    }
  } catch (e) { if (e?.name !== "AbortError") toast.error(errMsg(e)); }
};

const fmtDate = (s) => new Date(s).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });

const OrderDetail = ({ order, isAdmin, onStatus, onDelete, onClose }) => (
  <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
      {order && <>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-3">{order.order_no} <StatusBadge status={order.status} testId="order-detail-status" /></DialogTitle></DialogHeader>
        <div className="grid gap-2 text-sm sm:grid-cols-2">
          <p><span className="text-slate-500">Müşteri:</span> <b>{order.customer_name}</b></p>
          <p><span className="text-slate-500">Plasiyer:</span> {order.marketer_name}</p>
          {order.customer_phone && <p><span className="text-slate-500">Telefon:</span> {order.customer_phone}</p>}
          <p><span className="text-slate-500">Tarih:</span> {fmtDate(order.created_at)}</p>
          {(order.customer_tax_office || order.customer_tax_no) && <p><span className="text-slate-500">Vergi D./No:</span> {[order.customer_tax_office, order.customer_tax_no].filter(Boolean).join(" / ")}</p>}
          {order.customer_address && <p className="sm:col-span-2"><span className="text-slate-500">Adres:</span> {order.customer_address}</p>}
          {order.note && <p className="sm:col-span-2"><span className="text-slate-500">Not:</span> {order.note}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="order-pdf-download-button" variant="outline" onClick={() => downloadFile(`/orders/${order.id}/pdf`, `${order.order_no}.pdf`).catch((e) => toast.error(errMsg(e)))}><FileText className="mr-2 h-4 w-4" />PDF İndir</Button>
          <Button data-testid="order-pdf-share-button" className="bg-blue-600 hover:bg-blue-700" onClick={() => sharePdf(order)}><Share2 className="mr-2 h-4 w-4" />Paylaş / Yazdır</Button>
          {isAdmin && order.status === "İptal" && <Button data-testid="order-delete-button" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => onDelete(order)}><Trash2 className="mr-2 h-4 w-4" />Siparişi Sil</Button>}
        </div>
        {isAdmin && <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">Durumu değiştir:</span>
          <Select value={order.status} onValueChange={(v) => onStatus(order, v)}>
            <SelectTrigger className="w-48" data-testid="order-status-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`order-status-option-${s}`}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>}
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left font-mono text-[11px] uppercase text-slate-500">
              <tr><th className="p-2">Ürün</th><th className="p-2 text-right">Miktar</th><th className="p-2 text-right">Fiyat</th><th className="p-2 text-right">İskonto</th><th className="p-2 text-right">Net</th></tr>
            </thead>
            <tbody>{order.items.map((i) => (
              <tr key={i.product_id} className="border-t border-slate-100">
                <td className="p-2"><b>{i.name}</b><div className="font-mono text-[11px] text-slate-500">{i.code}</div></td>
                <td className="p-2 text-right">{i.quantity} {i.unit}</td><td className="p-2 text-right">{money(i.price)}</td>
                <td className="p-2 text-right">{i.discount ? discLabel(i.discount, i.discount_type) : "-"}</td><td className="p-2 text-right font-semibold">{money(i.net)}</td>
              </tr>))}</tbody>
          </table>
        </div>
        <div className="ml-auto w-full max-w-xs space-y-1 text-sm">
          {[["Brüt", order.gross_total], ["Satır İskonto", -order.line_discount_total], [`Genel İskonto (${discLabel(order.general_discount, order.general_discount_type)})`, -order.general_discount_total], ["Ara Toplam", order.subtotal], ["KDV", order.vat_total]].map(([k, v]) => (
            <div key={k} className="flex justify-between text-slate-600"><span>{k}</span><span>{money(v)}</span></div>))}
          <div className="flex justify-between border-t pt-2 font-display text-lg font-bold"><span>Toplam</span><span data-testid="order-detail-grand-total">{money(order.grand_total)}</span></div>
        </div>
      </>}
    </DialogContent>
  </Dialog>
);

export default function OrdersPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const params = { status, q, date_from: from, date_to: to };
  const load = () => api.get("/orders", { params }).then((r) => setOrders(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [status, q, from, to]); // eslint-disable-line
  const total = orders.filter((o) => o.status !== "İptal").reduce((a, o) => a + o.grand_total, 0);

  const changeStatus = async (o, s) => {
    try {
      const { data } = await api.patch(`/orders/${o.id}/status`, { status: s });
      setSel(data); toast.success(`Durum: ${s}`); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteOrder = async (o) => {
    if (!window.confirm(`${o.order_no} kalıcı olarak silinsin mi?`)) return;
    try { await api.delete(`/orders/${o.id}`); toast.success("Sipariş silindi"); setSel(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteCancelled = async () => {
    if (!window.confirm("İptal edilen TÜM siparişler kalıcı olarak silinsin mi?")) return;
    try { const { data } = await api.delete("/orders/cancelled"); toast.success(`${data.deleted} iptal sipariş silindi`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title={isAdmin ? "Siparişler" : "Siparişlerim"} kicker={`${orders.length} sipariş`}>
        {isAdmin && <Button data-testid="orders-delete-cancelled-button" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={deleteCancelled}>
          <Trash2 className="mr-2 h-4 w-4" /> İptalleri Sil
        </Button>}
        <Button data-testid="orders-export-button" variant="outline" onClick={() => downloadFile("/orders/excel/export", "siparisler.xlsx", params)}>
          <Download className="mr-2 h-4 w-4" /> Excel'e Aktar
        </Button>
      </PageHeader>
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="orders-date-filter">
        <div><label className="text-[10px] font-mono uppercase text-slate-500">Başlangıç Tarihi</label>
          <Input data-testid="orders-date-from-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 w-44" /></div>
        <div><label className="text-[10px] font-mono uppercase text-slate-500">Bitiş Tarihi</label>
          <Input data-testid="orders-date-to-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 w-44" /></div>
        {[["Bugün", 0], ["Son 7 Gün", 6], ["Son 30 Gün", 29]].map(([l, d]) => (
          <Button key={l} type="button" size="sm" variant="outline" data-testid={`orders-date-quick-${d}`} onClick={() => {
            const f = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
            const now = new Date(); const st = new Date(); st.setDate(now.getDate() - d); setFrom(f(st)); setTo(f(now));
          }}>{l}</Button>
        ))}
        {(from || to) && <Button type="button" size="sm" variant="ghost" data-testid="orders-date-clear" onClick={() => { setFrom(""); setTo(""); }}>Temizle</Button>}
        <p className="ml-auto text-sm text-slate-600">Toplam (iptal hariç): <b className="font-display text-base text-slate-950" data-testid="orders-filtered-total">{money(total)}</b></p>
      </div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="orders-search-input" placeholder={user.role === "customer" ? "Sipariş no ara..." : "Sipariş no, müşteri veya plasiyer ara..."} value={q} onChange={(e) => setQ(e.target.value)} className="h-11 bg-white pl-10" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {["", ...STATUSES].map((s) => (
            <button key={s || "all"} data-testid={`orders-filter-${s || "all"}`} onClick={() => setStatus(s)}
              className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-colors ${status === s ? "border-slate-950 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>{s || "Tümü"}</button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left font-mono text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="p-4">Sipariş No</th><th className="p-4">Tarih</th><th className="p-4">Müşteri</th>{isAdmin && <th className="p-4">Plasiyer</th>}<th className="p-4">Durum</th><th className="p-4 text-right">Toplam</th></tr>
          </thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={6} className="p-12 text-center text-slate-500" data-testid="orders-empty">Sipariş bulunamadı</td></tr>}
            {orders.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.order_no}`} onClick={() => setSel(o)} className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-blue-50/50">
                <td className="p-4 font-mono font-semibold whitespace-nowrap">{o.order_no}</td>
                <td className="p-4 text-slate-500 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                <td className="p-4 font-medium">{o.customer_name}</td>
                {isAdmin && <td className="p-4 text-slate-600">{o.marketer_name}</td>}
                <td className="p-4"><StatusBadge status={o.status} testId={`order-status-badge-${o.order_no}`} /></td>
                <td className="p-4 text-right font-display font-bold whitespace-nowrap">{money(o.grand_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <OrderDetail order={sel} isAdmin={isAdmin} onStatus={changeStatus} onDelete={deleteOrder} onClose={() => setSel(null)} />
    </div>
  );
}
